// Custom hook for file upload functionality
import { useState, useCallback } from 'react';
import { useFileStore } from '@stores/useFileStore';
import { useAuthStore } from '@stores/useAuthStore';
import { AudioFile, FileType, Tag, FileStatus } from '@types';
import { getDurationFromFile } from '@utils/audio';
import { validateAudioFile, sanitizeFileName } from '@utils/validation';
import { captureException } from '@utils/sentry';
import { formatBytes } from '@utils/format';
import { supabase } from '@config/supabase';
import { showSuspensionToastIfNeeded } from '@utils/suspension';
import { useToast } from '@components/ui';
import type { UploadBatchItem, UploadBatchResult } from '@features/upload/wizard/types';

export interface UseFileUploadReturn {
  isUploading: boolean;
  error: string | null;
  uploadFile: (file: File, type: FileType, additionalMetadata?: Partial<AudioFile>) => Promise<void>;
  uploadMultiple: (files: File[], type: FileType, tags?: Tag[], manualMetadata?: Partial<AudioFile>) => Promise<void>;
  uploadBatch: (items: UploadBatchItem[], type: FileType) => Promise<UploadBatchResult[]>;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { uploadFile: uploadToStore } = useFileStore();
  const { error: showErrorToast } = useToast();

  const uploadFile = useCallback(
    async (file: File, type: FileType, additionalMetadata?: Partial<AudioFile>) => {
      setIsUploading(true);
      setError(null);

      try {
        const isSuspended = await showSuspensionToastIfNeeded(showErrorToast);
        if (isSuspended) {
          throw new Error('ACCOUNT_SUSPENDED');
        }

        const { user } = useAuthStore.getState();
        if (user?.id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('overflow_state, storage_used, storage_limit, downgrade_warning_started_at')
            .eq('id', user.id)
            .single();

          if (profile) {
            const hasDowngradeWarning = !!profile.downgrade_warning_started_at;
            const storageUsed = profile.storage_used || 0;
            const storageLimit = profile.storage_limit || 0;
            const isActualOverflow = storageUsed > storageLimit;
            const isNormalOverflow = profile.overflow_state && profile.overflow_state !== 'none';

            if (isActualOverflow && isNormalOverflow && !hasDowngradeWarning) {
              const overflowAmount = storageUsed - storageLimit;
              const overflowText = overflowAmount > 0 ? ` (+${formatBytes(overflowAmount)} exceeded)` : '';
              throw new Error(`STORAGE_OVERFLOW: Your storage limit has been exceeded. You are using ${formatBytes(storageUsed)} of ${formatBytes(storageLimit)}${overflowText}. Please delete files or upgrade your plan.`);
            }
          }
        }

        const validation = validateAudioFile(file);
        if (!validation.valid) {
          const error = new Error(validation.error || 'Invalid audio file format');
          captureException(error, { fileName: file.name, fileSize: file.size, fileType: file.type });
          throw error;
        }

        const sanitizedName = sanitizeFileName(file.name);
        const duration = await getDurationFromFile(file);

        const status: FileStatus | undefined = additionalMetadata?.status
          ? (typeof additionalMetadata.status === 'string'
              ? additionalMetadata.status as FileStatus
              : additionalMetadata.status)
          : undefined;

        const metadata: Partial<AudioFile> = {
          name: sanitizedName,
          originalName: file.name,
          type,
          size: file.size,
          duration,
          format: file.type.includes('mp3') ? 'mp3_320' as any : 'wav' as any,
          bpm: additionalMetadata?.bpm,
          key: additionalMetadata?.key,
          isStarred: false,
          isTrashed: false,
          isPublic: false,
          downloads: 0,
          plays: 0,
          tags: additionalMetadata?.tags || [],
          versionType: additionalMetadata?.versionType,
          groupId: additionalMetadata?.groupId,
          isMainVersion: additionalMetadata?.isMainVersion,
          ...additionalMetadata,
          status: status || additionalMetadata?.status,
        };

        await uploadToStore(file, metadata);
        setIsUploading(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed');
        captureException(error, { fileName: file.name, fileType: type });
        const errorMessage = error.message;
        setError(errorMessage);
        setIsUploading(false);
        throw error;
      }
    },
    [uploadToStore]
  );

  const uploadBatch = useCallback(
    async (items: UploadBatchItem[], type: FileType): Promise<UploadBatchResult[]> => {
      setIsUploading(true);
      setError(null);

      const results = await Promise.allSettled(
        items.map(async ({ file, metadata }) => {
          await uploadFile(file, type, metadata);
          return file.name;
        })
      );

      const batchResults: UploadBatchResult[] = results.map((result, index) => {
        const fileName = items[index].file.name;
        if (result.status === 'fulfilled') {
          return { fileName, success: true };
        }
        const err = result.reason;
        return {
          fileName,
          success: false,
          error: err instanceof Error ? err.message : 'Upload failed',
        };
      });

      const anyFailed = batchResults.some((r) => !r.success);
      if (!anyFailed) {
        setIsUploading(false);
      } else if (batchResults.every((r) => !r.success)) {
        setError(batchResults[0]?.error || 'Upload failed');
        setIsUploading(false);
      } else {
        setIsUploading(false);
      }

      return batchResults;
    },
    [uploadFile]
  );

  const uploadMultiple = useCallback(
    async (files: File[], type: FileType, tags?: Tag[], manualMetadata?: Partial<AudioFile>) => {
      const items: UploadBatchItem[] = files.map((file) => ({
        file,
        metadata: { tags: tags || [], ...manualMetadata },
      }));
      const results = await uploadBatch(items, type);
      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        throw new Error(failed[0].error || 'Multiple upload failed');
      }
    },
    [uploadBatch]
  );

  return {
    isUploading,
    error,
    uploadFile,
    uploadMultiple,
    uploadBatch,
  };
};
